import {
  DatePicker,
  Field,
  FieldLabel,
  LeaderGroupCombobox,
  ModuleWrapper,
  UserCombobox,
} from '@/components'
import { PlainLink, SortableHeader, Table } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { PageType } from '@/modules/common/consts'
import { Module } from '@/modules/companies'
import {
  asNullableString,
  asString,
  asStringArrayOrEmpty,
  parseDateRangeOrDefault,
} from '@/utils'
import { getNodes } from '@/utils/graphql'
import { format } from '@/utils/date-fns'
import {
  useAdmin,
  useAutoSelectSingleLeaderGroup,
  useInfiniteScroll,
  useLeaderGroups,
  useOnErrorAuto,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination'
import { PERMISSIONS } from '@/utils/permissions'
import { NetworkStatus, useQuery } from '@apollo/client'
import {
  addDays,
  endOfMonth,
  formatISO,
  startOfDay,
  startOfMonth,
} from 'date-fns'
import React, { useCallback, useMemo } from 'react'
import { isDesktop } from 'react-device-detect'
import styled from 'styled-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { ALL_WORK_DAY_INFRINGEMENTS_QUERY } from '../queries'
import { getWorkDayCategoryDisplayIdentifier } from '../work-day-category-labels'

const FilterSection = styled.section`
  display: flex;
  gap: 0.75rem;

  & > * {
    max-width: 100%;
  }
`

const Controls = styled(Card)`
  width: 100%;
  box-sizing: border-box;
  overflow-x: auto;
  gap: 1rem;
  margin-bottom: 1rem;
  display: flex;
  flex-wrap: wrap;
`

const DescriptionCell = styled.td`
  max-width: 420px;

  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const EmployeeName = styled.span`
  display: block;
  font-weight: 600;
`

const EmployeePhone = styled.span`
  color: ${props => props.theme.colors.gray6};
  display: block;
  font-size: 0.875rem;
`

const DEFAULT_DATE_RANGE = {
  from: startOfMonth(new Date()),
  to: endOfMonth(new Date()),
}

interface WorkDayInfringementsFilter {
  orderBy: string
  users: string[]
  dateRange: {
    from: Date
    to: Date
  }
  leaderGroup: string | null
}

export const WorkDayInfringements: React.FC = () => {
  const translations = useTranslate({
    title: 'infringements.work-day-infringements',
    noWorkDayInfringements: 'infringements.no-work-day-infringements',
    users: 'common.users',
    leaderGroup: 'common.leader-group',
    dateRange: 'common.date-range',
    date: 'common.date',
    employee: 'common.employee',
    rule: 'infringements.rule',
    basis: 'common.basis',
    open: 'common.open-alt',
    workDayCategoryOverwork: 'infringements.work-day-category.overwork',
    workDayCategoryPause: 'infringements.work-day-category.pause',
  })
  const me = useUser()
  const admin = useAdmin()
  const { hasPermissions } = usePermissions()
  const onErrorAuto = useOnErrorAuto()
  const canViewOthers = hasPermissions(
    PERMISSIONS.infringements.view.othersinfringement,
    true
  )
  const {
    formValues: filter,
    formChangeHandler: filterHandler,
    updateForm,
    loadStoredFormState,
  } = useForm<WorkDayInfringementsFilter>({
    values: {
      orderBy: '-infringementTimestamp',
      users: canViewOthers ? [] : [me.id],
      dateRange: DEFAULT_DATE_RANGE,
      leaderGroup: null,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'work-day-infringement-filters',
        sessionStorage: true,
        transformValues: {
          orderBy: asString,
          users: asStringArrayOrEmpty,
          dateRange: parseDateRangeOrDefault,
          leaderGroup: asNullableString,
        },
      },
    },
  })

  const { myLeaderGroups, groupsLoading } = useLeaderGroups(
    Module.WORK_DAY_INFRINGEMENTS
  )
  const hasLeaderGroups = myLeaderGroups.length > 0
  const selectedLeaderGroupUsers = useMemo(
    () =>
      new Set(
        myLeaderGroups
          .find(group => group.id === filter.leaderGroup)
          ?.users.map(user => user.id) ?? []
      ),
    [filter.leaderGroup, myLeaderGroups]
  )

  useAutoSelectSingleLeaderGroup({
    canViewOthers: canViewOthers && hasLeaderGroups && !groupsLoading,
    isAdmin: admin,
    leaderGroup: filter.leaderGroup ?? undefined,
    leaderGroups: myLeaderGroups,
    loadStoredFormState,
    updateForm,
    onAutoSelect: group => {
      updateForm({ leaderGroup: group.id })
    },
  })

  const handleLeaderGroupChange = (value: string) => {
    updateForm({
      leaderGroup: value || null,
      users: [],
    })
  }

  const datetimeStartGte = startOfDay(filter.dateRange.from)
  const datetimeEndLt = addDays(startOfDay(filter.dateRange.to), 1)
  const { data, loading, networkStatus, fetchMore } = useQuery(
    ALL_WORK_DAY_INFRINGEMENTS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        orderBy: filter.orderBy,
        first: DEFAULT_PAGE_SIZE,
        users: canViewOthers ? filter.users : [me.id],
        archivedAt_Isnull: true,
        infringementTimestamp_Lt: formatISO(datetimeEndLt),
        infringementTimestamp_Gte: formatISO(datetimeStartGte),
        ...(canViewOthers &&
          filter.users.length === 0 &&
          filter.leaderGroup !== null && {
            leaderGroupId: filter.leaderGroup,
          }),
      },
      onError: onErrorAuto(),
    }
  )
  const pageInfo = data?.allWorkInfringements.pageInfo
  const infringements = getNodes(data?.allWorkInfringements)
  const cacheLoading =
    (loading && !data) || networkStatus === NetworkStatus.refetch

  const handleScrollBottom = useCallback(async () => {
    if (!pageInfo?.hasNextPage) return
    await fetchMore({
      variables: {
        after: pageInfo.endCursor,
      },
    })
  }, [fetchMore, pageInfo])

  useInfiniteScroll(handleScrollBottom, 200, !loading && pageInfo?.hasNextPage)

  return (
    <ModuleWrapper title={translations.title} page={PageType.INFRINGEMENTS}>
      <Controls>
        <FilterSection className="flex-wrap">
          {isDesktop && canViewOthers && (
            <Field>
              <FieldLabel>{translations.users}</FieldLabel>
              <UserCombobox
                value={filter.users}
                mode="multiple"
                className="min-w-[260px] max-w-full"
                onChange={value => updateForm({ users: value })}
                hideItem={item =>
                  !!filter.leaderGroup && !selectedLeaderGroupUsers.has(item.id)
                }
              />
            </Field>
          )}

          {hasLeaderGroups && canViewOthers && (
            <Field>
              <FieldLabel>{translations.leaderGroup}</FieldLabel>
              <LeaderGroupCombobox
                mode="single"
                module={Module.WORK_DAY_INFRINGEMENTS}
                value={filter.leaderGroup ?? ''}
                clearable
                onChange={handleLeaderGroupChange}
              />
            </Field>
          )}

          <Field>
            <FieldLabel>{translations.dateRange}</FieldLabel>
            <DatePicker
              mode="range"
              value={filter.dateRange}
              format="PP"
              onChange={value => {
                if (!value?.from || !value.to) return
                filterHandler('dateRange')({ from: value.from, to: value.to })
              }}
            />
          </Field>
        </FilterSection>
      </Controls>

      <Table
        loading={cacheLoading}
        noData={infringements.length === 0}
        noDataText={translations.noWorkDayInfringements}
      >
        <thead>
          <tr>
            <SortableHeader
              baseValue="infringementTimestamp"
              sortValue={filter.orderBy}
              invertDirection
              onSort={filterHandler('orderBy')}
            >
              {translations.date}
            </SortableHeader>
            <SortableHeader
              baseValue="user__firstName"
              sortValue={filter.orderBy}
              disabled={!admin}
              onSort={filterHandler('orderBy')}
            >
              {translations.employee}
            </SortableHeader>
            <th>{translations.rule}</th>
            <th>{translations.basis}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {infringements.map(infringement => {
            const category = infringement.workInfringementCategory
            const displayIdentifier = getWorkDayCategoryDisplayIdentifier(
              category.identifier,
              {
                overwork: translations.workDayCategoryOverwork,
                pause: translations.workDayCategoryPause,
              }
            )

            return (
              <tr key={infringement.id}>
                <td>
                  <PlainLink
                    to={`/work-day-infringements/${infringement.id}`}
                    $hoverColor="secondary"
                  >
                    {format(
                      new Date(infringement.infringementTimestamp),
                      'dd.MM.yyyy HH:mm'
                    )}
                  </PlainLink>
                </td>
                <td>
                  <EmployeeName>{infringement.user.fullName}</EmployeeName>
                  {infringement.user.phoneNumber && (
                    <EmployeePhone>
                      {infringement.user.phoneNumber}
                    </EmployeePhone>
                  )}
                </td>
                <DescriptionCell>
                  <span>
                    <b>{displayIdentifier}.</b> {category.title}
                  </span>
                </DescriptionCell>
                <td>{category.legalBasis}</td>
                <td>
                  <PlainLink
                    to={`/work-day-infringements/${infringement.id}`}
                    $hoverColor="secondary"
                  >
                    {translations.open}
                  </PlainLink>
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </ModuleWrapper>
  )
}
